/**
 * Internal replacements for Rebass Styled Components.
 * Provides the same styled-system prop support as the Rebass library
 * (space, layout, flexbox, color, typography, border, position, shadow)
 * without the external rebass dependency.
 *
 * Prop typing intentionally mirrors the shape of @types/rebass (the DefinitelyTyped
 * package this codebase used to rely on): a "KnownProps" interface made up of the
 * relevant styled-system prop groups, combined with `Omit<React.HTMLProps<T>, keyof KnownProps>`
 * for the underlying element's own attributes -- Omit (rather than a plain intersection)
 * is what avoids type conflicts on overlapping attribute names like `color`, which
 * exists both as a deprecated global HTML attribute and as a styled-system prop.
 *
 * The exported components are declared as plain `React.FC<Props>` (via a cast), rather
 * than passing the props generic directly to `styled.div<Props>` et al. styled-components'
 * own generic overloads re-intersect the props generic with the *unmodified* intrinsic
 * element attributes (not the Omit-adjusted version above), which reintroduces the same
 * `color`-type conflict one level up. Casting sidesteps that; it's exactly what @types/rebass
 * did too, just declared directly as a .d.ts instead of a cast in source.
 */
import * as React from 'react';
import styled from 'styled-components';
import { space, layout, flexbox, color, typography, border, position, shadow, background } from 'styled-system';
import type {
  SpaceProps,
  LayoutProps,
  FlexboxProps,
  ColorProps,
  TypographyProps,
  BorderProps,
  PositionProps,
  ShadowProps,
  BackgroundProps,
} from 'styled-system';

type BoxKnownProps = BackgroundProps &
  BorderProps &
  ColorProps &
  FlexboxProps &
  LayoutProps &
  PositionProps &
  ShadowProps &
  SpaceProps &
  TypographyProps & {};

export type BoxProps = BoxKnownProps & Omit<React.HTMLProps<HTMLDivElement>, keyof BoxKnownProps | 'ref'> & {};

const StyledBox = styled.div`
  box-sizing: border-box;
  min-width: 0;
  ${space}
  ${layout}
  ${flexbox}
  ${color}
  ${typography}
  ${border}
  ${position}
  ${shadow}
  ${background}
`;

export const Box = (StyledBox as unknown) as React.FC<BoxProps>;

export type FlexProps = BoxProps & {};

const StyledFlex = styled(StyledBox)`
  display: flex;
`;

export const Flex = (StyledFlex as unknown) as React.FC<FlexProps>;

type TextKnownProps = BorderProps & ColorProps & LayoutProps & PositionProps & SpaceProps & TypographyProps & {};

export type TextProps = Omit<React.HTMLProps<HTMLParagraphElement>, keyof TextKnownProps | 'ref'> & TextKnownProps & {};

const StyledText = styled.div`
  margin: 0;
  ${space}
  ${layout}
  ${color}
  ${typography}
  ${border}
  ${position}
`;

export const Text = (StyledText as unknown) as React.FC<TextProps>;

type ImageKnownProps = BorderProps & ColorProps & LayoutProps & SpaceProps & {};

export type ImageProps = ImageKnownProps & Omit<React.HTMLProps<HTMLImageElement>, keyof ImageKnownProps | 'ref'> & {};

const StyledImage = styled.img`
  max-width: 100%;
  ${space}
  ${layout}
  ${border}
  ${color}
`;

export const Image = (StyledImage as unknown) as React.FC<ImageProps>;

type LinkKnownProps = BorderProps & ColorProps & LayoutProps & PositionProps & SpaceProps & TypographyProps & {};

export type LinkProps = LinkKnownProps & Omit<React.HTMLProps<HTMLAnchorElement>, keyof LinkKnownProps | 'ref'> & {};

const StyledLink = styled.a`
  ${space}
  ${layout}
  ${color}
  ${typography}
  ${border}
  ${position}
`;

export const Link = (StyledLink as unknown) as React.FC<LinkProps>;

type ButtonKnownProps = BorderProps & ColorProps & LayoutProps & SpaceProps & TypographyProps & {};

export type ButtonProps = ButtonKnownProps &
  Omit<React.HTMLProps<HTMLButtonElement>, keyof ButtonKnownProps | 'ref'> & {};

const StyledButton = styled.button`
  appearance: none;
  display: inline-block;
  text-align: center;
  line-height: inherit;
  text-decoration: none;
  font-size: inherit;
  margin: 0;
  padding: 6px 16px;
  border: 0;
  border-radius: 4px;
  ${space}
  ${layout}
  ${color}
  ${typography}
  ${border}
`;

export const Button = (StyledButton as unknown) as React.FC<ButtonProps>;
